import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, Trash2, X, Loader2 } from "lucide-react";
import { Button } from "./Button";
import type { LucideIcon } from "lucide-react";

export type ConfirmDialogVariant = "danger" | "warning" | "info";

interface ConfirmDialogProps {
  isOpen: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmDialogVariant;
  icon?: LucideIcon;
  isLoading?: boolean;
}

const variantConfig: Record<ConfirmDialogVariant, { color: string; bgColor: string; Icon: LucideIcon }> = {
  danger: {
    color: "text-red-500",
    bgColor: "bg-red-500/10",
    Icon: Trash2,
  },
  warning: {
    color: "text-[#284899]",
    bgColor: "bg-[#284899]/10",
    Icon: AlertTriangle,
  },
  info: {
    color: "text-blue-500",
    bgColor: "bg-blue-500/10",
    Icon: AlertTriangle,
  },
};

export function ConfirmDialog({
  isOpen,
  onConfirm,
  onCancel,
  title,
  message,
  confirmText = "Conferma",
  cancelText = "Annulla",
  variant = "danger",
  icon,
  isLoading = false,
}: ConfirmDialogProps) {
  const config = variantConfig[variant];
  const IconComponent = icon || config.Icon;

  const handleConfirm = async () => {
    await onConfirm();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onCancel}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 pb-4">
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl ${config.bgColor} flex items-center justify-center flex-shrink-0`}>
                  <IconComponent className={`h-6 w-6 ${config.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg font-semibold">{title}</h2>
                  <p className="text-sm text-muted-foreground mt-1">{message}</p>
                </div>
                <button
                  onClick={onCancel}
                  className="p-1 rounded-lg hover:bg-secondary/50 transition-colors text-muted-foreground hover:text-foreground"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="px-6 pb-6 flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={onCancel}
                disabled={isLoading}
              >
                {cancelText}
              </Button>
              <Button
                className={`flex-1 ${variant === "danger"
                    ? "bg-red-500 hover:bg-red-600 text-white"
                    : variant === "warning"
                      ? "bg-[#284899] hover:bg-[#1e3570] text-white"
                      : ""
                  }`}
                onClick={handleConfirm}
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                {confirmText}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Hook for easier usage
import { useState, useCallback } from "react";

interface UseConfirmDialogOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmDialogVariant;
  icon?: LucideIcon;
}

export function useConfirmDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<UseConfirmDialogOptions>({
    title: "",
    message: "",
  });
  const [resolveRef, setResolveRef] = useState<((value: boolean) => void) | null>(null);

  const confirm = useCallback((opts: UseConfirmDialogOptions): Promise<boolean> => {
    setOptions(opts);
    setIsOpen(true);
    return new Promise((resolve) => {
      setResolveRef(() => resolve);
    });
  }, []);

  const handleConfirm = useCallback(async () => {
    setIsLoading(true);
    resolveRef?.(true);
    setIsOpen(false);
    setIsLoading(false);
  }, [resolveRef]);

  const handleCancel = useCallback(() => {
    resolveRef?.(false);
    setIsOpen(false);
  }, [resolveRef]);

  const DialogComponent = useCallback(() => (
    <ConfirmDialog
      isOpen={isOpen}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      isLoading={isLoading}
      {...options}
    />
  ), [isOpen, handleConfirm, handleCancel, isLoading, options]);

  return {
    confirm,
    ConfirmDialog: DialogComponent,
  };
}

